import React, { useEffect, useState } from 'react';
import { ComplexDialog, Squish } from '@grubhubprod/gh-cookbook-react';
import { publish } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import { DialogHeader } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/AddEmployeesDialog/DialogHeader';
import { DialogFooter } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/AddEmployeesDialog/DialogFooter';
import { DownloadSection } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/AddEmployeesDialog/DownloadSection';
import { UploadSection } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/AddEmployeesDialog/UploadSection';
import { MODAL_Z_INDEX } from 'src/components/Modal/Modal';
import { RESET_UPLOAD_CSV_PROCESS } from 'src/components/CSVUploadTracking/CSVUploadTracking';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

export const HEADING = 'Add list of employees';
export const CONFIRM_LABEL = 'Done';

interface AddEmployeesDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  onFileUploadFinished: () => void;
}

export function AddEmployeesDialog({
  isOpen,
  onConfirm,
  onDismiss,
  onFileUploadFinished,
}: AddEmployeesDialogProps): JSX.Element {
  const [isUploadAnotherCSVDisabled, setIsUploadAnotherCSVDisabled] =
    useState(true);
  const [isFileUploadedSuccessfully, setIsFileUploadedSuccessfully] =
    useState(false);
  const { sendTealiumPageView } = useAnalytics();

  const handleUploadAnotherCSVClick = (): void => {
    publish(RESET_UPLOAD_CSV_PROCESS);
    setIsUploadAnotherCSVDisabled(true);
  };

  const resetLocalState = (): void => {
    setIsUploadAnotherCSVDisabled(true);
    setIsFileUploadedSuccessfully(false);
  };

  const handleFileUploadError = (): void => {
    resetLocalState();
    onFileUploadFinished();
  };

  const handleFileUploadSuccess = (): void => {
    setIsUploadAnotherCSVDisabled(false);
    setIsFileUploadedSuccessfully(true);
    onFileUploadFinished();
  };

  useEffect(() => {
    if (!isOpen) {
      resetLocalState();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      sendTealiumPageView({
        pagegroup: 'corporate admin',
        pagesubgroup: 'manage employees_upload',
      });
    }
  }, [isOpen, sendTealiumPageView]);

  return (
    <ComplexDialog
      isOpen={isOpen}
      onDismiss={onDismiss}
      aria-label={HEADING}
      zindex={MODAL_Z_INDEX}
      fullScreenMobile={false}
      clickOutsideToClose={false}
      title={HEADING}
      renderDialogHeader={(): JSX.Element => (
        <DialogHeader onDismiss={onDismiss} heading={HEADING} />
      )}
      renderDialogFooter={(): JSX.Element => (
        <DialogFooter
          confirmLabel={CONFIRM_LABEL}
          onConfirm={onConfirm}
          isConfirmButtonDisabled={!isFileUploadedSuccessfully}
          isUploadAnotherCSVDisabled={isUploadAnotherCSVDisabled}
          onUploadAnotherCSV={handleUploadAnotherCSVClick}
        />
      )}
    >
      <Squish inset="md" width="700px">
        <DownloadSection />
        <UploadSection
          onFileUploadSuccess={handleFileUploadSuccess}
          onFileUploadError={handleFileUploadError}
        />
      </Squish>
    </ComplexDialog>
  );
}
